<template>
  <AppLayout
    :access-status="!currentUserChecked ? 'pending' : isAllowedToViewLicenses ? 'granted' : 'blocked'"
    :warning-message="city?.isDemoMode ? `${city.name} is in demo mode.` : null"
    :wide-left-bar="hasSelection"
    :skip-tour="!currentUserChecked || !isAuthenticated"
    :show-detail="hasSelection"
    :show-list="!embedded && !hasSelection"
    show-map
  >
    <template v-if="!embedded" #top>
      <TopBar
        :city="city"
        selected-tab="licenses"
        :buttons="buttons"
        :show-search="!hasSelection"
        @search="search"
        search-placeholder="Search licenses"
      />
      <div v-if="exportError" class="text-center text-danger p-3" role="alert">
        Could not export licenses: {{ exportError.message || String(exportError) }}
      </div>
      <EditLicenseModal v-if="isAuthenticated" :license="editLicense" @saved="licenseCreated" @closed="editLicense=null" />
      <ImportLicensesModal v-if="isAuthenticated" :city="city" v-model:show="showImportLicenseModal" />
    </template>

    <template v-if="!embedded" #list>
      <FilterableList
        :count="total"
        :page-start="pageStart"
        :page-end="pageEnd"
        @previous-page="changePage(previousCursor)"
        @next-page="changePage(nextCursor)"
        @updated="applyFilterChanges"
        @canceled="cancelFilterChanges"
        @reset="resetFilters"
        :active-filter-count="activeFilterCount"
        noun="licenses"
        :query="query"
        :loading="!loaded || filtering"
        :show-header="!hasSelection"
      >
        <template #filters v-if="draftFilter">
          <FilterEditor :filters="draftFilter" :sections="licenseFilterSections" />
        </template>
        <template #list>
          <div class="d-flex flex-row align-items-stretch h-100">
            <div class="w-100">
              <LoadingMessage v-if="!loaded" message="Loading licenses"/>
              <LoadingMessage v-else-if="filtering" message="Updating licenses" />
              <div v-if="loadError" class="text-center text-danger p-3" role="alert">
                Could not load licenses: {{ loadError.message || String(loadError) }}
                <button type="button" class="btn btn-link" @click="city ? reloadLicenses() : reloadCity()">Retry</button>
              </div>
              <BCollapse :show="loaded && !filtering && !loadError && total === 0">
                <div class="d-flex flex-column align-items-center p-4">
                  <h6 class="text-center">
                    No licenses match the current search and filters.
                  </h6>
                  <p class="text-center">
                    Try changing the search or filters to see more licenses.
                  </p>
                </div>
              </BCollapse>

              <LicenseList
                v-if="!filtering"
                :licenses="licenses"
                :selected-id="selectedLicenseId"
                @select="selectLicense"
              />
            </div>
          </div>
        </template>
      </FilterableList>
    </template>

    <template v-if="selectedLicenseId" #detail>
      <LicenseDetailView
        :detail-city="city"
        :license-id="selectedLicenseId"
        :panel="panel"
        @update:panel="updatePanel"
        @close="closeDetail"
        @show-location="showLocation"
        class="w-100"
      >
        <template #navigation>
          <IconButton
            icon="x-lg"
            title="Back to list"
            class="flex-shrink-0"
            @click="closeDetail"
          />
          <div class="text-muted small mx-1 text-nowrap">
            <template v-if="selectedLicenseIndex >= 0">{{ selectedLicenseIndex + 1 }} of {{ total }}</template>
            <template v-else>Outside current results</template>
          </div>
          <IconButton button :disabled="filtering || !total" icon="chevron-up" class="flex-shrink-0" @click="() => nextLicense(true)" />
          <IconButton button :disabled="filtering || !total" icon="chevron-down" class="flex-shrink-0" @click="() => nextLicense(false)" />
        </template>
      </LicenseDetailView>
    </template>

    <template #map>
      <GMap
        ref="map"
        @deselect="selectLicense(null)"
        :city="city"
        :selected-marker-id="selectedLicenseId"
        :items="filtering ? [] : licenses"
        disable-left-bar-toggle
      >
        <template #marker="{item:license}">
          <GMapLicenseMarker :license="license" :selected-id="selectedLicenseId" />
        </template>
        <template #info="{data:license}">
          <LicenseItemView :license="license" />
        </template>
      </GMap>
    </template>
  </AppLayout>
</template>
<script>
import AppLayout from "@/components/common/AppLayout.vue";
import TopBar from "@/components/common/TopBar.vue";
import LicenseList from "@/components/LicenseList.vue";
import GMap from "@/components/common/GMap.vue";
import BCollapse from "@/components/common/BCollapse.vue";
import LicenseItemView from "@/components/LicenseItemView.vue";
import EditLicenseModal from "@/components/modals/EditLicenseModal.vue";
import {License, LicenseFilter} from "@/models/License";
import {generateCsvBlob} from "@/csv";

import LicenseDetailView from "@/components/LicenseDetailView.vue";
import IconButton from "@/components/common/IconButton.vue";
import GMapLicenseMarker from "@/components/common/GMapLicenseMarker.vue";
import LoadingMessage from "@/components/common/LoadingMessage.vue";
import ImportLicensesModal from "@/components/modals/ImportLicensesModal.vue";
import FilterableList from "@/components/common/FilterableList.vue";
import FilterEditor from "@/components/common/FilterEditor.vue";
import licenseFilterSections from "@/components/licenses/licenseFilterSections";
import {cityRepository, licenseRepository} from "@/data";
import {revalidateListOnDetailExit} from "@/detailListRefresh";

export default {
  components: {
    FilterEditor,
    FilterableList,
    ImportLicensesModal,
    LoadingMessage,
    GMapLicenseMarker,
    IconButton,
    LicenseDetailView,
    EditLicenseModal,
    LicenseItemView,
    BCollapse,
    GMap,
    LicenseList,
    TopBar,
    AppLayout
  },
  // TODO Find out how to make the following extensible from a base component
  props: {
    cityId: String,
    selectedLicenseId: String,
    onlyShowPending: Boolean,
    onlyShowMissingPropertyId: Boolean,
    onlyShowActive: Boolean,
    embedded: Boolean,
    panel: String
  },
  data() {
    return {
      loaded: false,
      loadError: null,
      city: null,
      // Custom data props for this component:
      editLicense: null,
      showImportLicenseButton: false,
      showImportLicenseModal: false,
      tour: null,
      query: '',
      openSingleSearchResult: false,
      filtering: false,
      licenses: [],
      total: 0,
      pageStart: 0,
      cursor: null,
      nextCursor: null,
      previousCursor: null,
      firstCursor: null,
      lastCursor: null,
      loadToken: 0,
      cityLoadToken: 0,
      exporting: false,
      exportError: null,
      exportUrl: null,
      filter: null,
      draftFilter: null
    };
  },
  watch: {
    cityId() {
      this.reloadCity();
    },
    licenseAuthState() {
      this.loadToken++;
      this.licenses = [];
      this.total = 0;
      this.filtering = false;
      if (this.city && this.isAllowedToViewLicenses) this.reloadLicenses({ selectedId: this.selectedLicenseId });
    },
    async selectedLicenseId(currentId, previousId) {
      if (currentId) {
        if (!this.licenses.some(license => license.id === currentId)) {
          await this.reloadLicenses({ selectedId: currentId });
        }
      } else {
        await revalidateListOnDetailExit({
          currentId, previousId, revalidate: () => this.reloadLicenses()
        });
      }
    },
    onlyShowPending() { this.resetPresetFilters(); },
    onlyShowActive() { this.resetPresetFilters(); },
    onlyShowMissingPropertyId() { this.resetPresetFilters(); },
    filter() {
      this.draftFilter = this.filter?.copy() || null;
    }
  },
  mounted() {
    this.reloadCity();
  },
  beforeUnmount() {
    this.loadToken++;
    this.cityLoadToken++;
    this.clearExportUrl();
  },
  // End extensible piece here
  computed: {
    licenseAuthState() {
      return !this.currentUserChecked ? 'pending' : this.isAuthenticated ? 'authenticated' : 'anonymous';
    },
    licenseFilterSections() {
      return licenseFilterSections;
    },
    isAllowedToViewLicenses() {
      return this.isAuthenticated || (this.embedded && this.city?.isLicenseListPublic);
    },
    statuses() {
      return this.city?.licenseStatuses || [];
    },
    buttons() {
      if (this.isAuthenticated) {
        return [
          {
            id: 'add',
            text: 'Add License',
            icon: 'plus-lg',
            click: () => this.addLicense(),
            variant: 'primary',
            showOnMobile: true
          },
          !this.showImportLicenseButton ? null : {
            id: 'import',
            icon: 'upload',
            tooltip: 'Import licenses by uploading a spreadsheet',
            click: () => this.showImportLicenseModal = true,
          },
          {
            id: 'export',
            click: () => this.exportLicenses(),
            tooltip: this.exporting ? 'Preparing CSV export…' : 'Export all matching licenses to CSV',
            text: this.exporting ? 'Exporting…' : null,
            icon: 'download',
            showOnMobile: true
          }
        ].filter(button => !!button);
      } else {
        return [];
      }
    },
    selectedLicense() {
      return this.licenses.find(license => license.id === this.selectedLicenseId) || null;
    },
    hasSelection() {
      return Boolean(this.selectedLicenseId);
    },
    activeFilterCount() {
      if (!this.filter || !this.city) return 0;
      let count = 0;
      const defaults = new LicenseFilter(this.city, null);
      if (this.filter.sortField !== defaults.sortField) count++;
      if (this.filter.onlyShowMissingProperties !== defaults.onlyShowMissingProperties) count++;
      const statusChanged = this.city.licenseStatuses?.some(s =>
        this.filter.status[s.code] !== defaults.status[s.code]
      );
      if (statusChanged) count++;
      const licenseTypeChanged = this.filter.licenseTypeOptions.some(type =>
        this.filter.licenseType[type.value] !== defaults.licenseType[type.value]
      );
      if (licenseTypeChanged) count++;
      for (const field of this.filter.customFields) {
        const changed = field.options.some(opt =>
          this.filter[field.id][opt] !== defaults[field.id][opt]
        );
        if (changed) count++;
      }
      return count;
    },
    selectedLicenseIndex() {
      const index = this.licenses.findIndex(license => license.id === this.selectedLicenseId);
      return index < 0 ? -1 : this.pageStart + index;
    },
    pageSize() { return 100; },
    pageEnd() { return this.pageStart + this.licenses.length; }
  },
  methods: {
    makeDefaultFilter() {
      const filter = new LicenseFilter(this.city, null);
      if (this.onlyShowPending) filter.reset('pending');
      if (this.onlyShowActive) filter.reset('active');
      filter.onlyShowMissingProperties = this.onlyShowMissingPropertyId;
      return filter;
    },
    resetPresetFilters() {
      if (!this.city) return;
      this.filter = this.makeDefaultFilter();
      return this.filterLicenses();
    },
    searchOptions() {
      return {
        query: this.query,
        sortField: this.filter.sortField,
        sortAscending: this.filter.sortAscending,
        statusAllowlist: { ...this.filter.status },
        licenseTypeAllowlist: { ...this.filter.licenseType },
        onlyShowMissingProperties: this.filter.onlyShowMissingProperties,
        customFieldOptions: this.filter.customFieldOptionMap
      };
    },
    async reloadCity() {
      const token = ++this.cityLoadToken;
      this.loadToken++;
      this.openSingleSearchResult = false;
      this.loaded = false;
      this.filtering = false;
      this.loadError = null;
      this.exportError = null;
      this.clearExportUrl();
      this.licenses = [];
      this.total = 0;
      this.pageStart = 0;
      this.cursor = this.nextCursor = this.previousCursor = null;
      this.firstCursor = this.lastCursor = null;
      this.city = null;
      this.filter = null;
      this.showImportLicenseButton = false;
      try {
        const city = await cityRepository.loadCity(this.cityId, true);
        if (token !== this.cityLoadToken) return;
        this.city = city;
        this.filter = this.makeDefaultFilter();
        const imports = city.canImportLicenses().then(supported => {
          if (token === this.cityLoadToken) this.showImportLicenseButton = supported;
        }).catch(error => console.error('Could not check license import support', error));
        await this.reloadLicenses({ offset: 0, selectedId: this.selectedLicenseId });
        await imports;
      } catch (error) {
        if (token !== this.cityLoadToken) return;
        this.loadError = error;
        this.loaded = true;
      }
    },
    async reloadLicenses({ cursor = null, offset = this.pageStart, selectedId = null } = {}) {
      if (!this.city || !this.filter || !this.currentUserChecked || !this.isAllowedToViewLicenses) return false;
      const token = ++this.loadToken;
      this.filtering = true;
      this.loadError = null;
      try {
        const result = await licenseRepository.searchLicenses(this.cityId, {
          ...this.searchOptions(), cursor, offset, selectedId
        });
        if (token !== this.loadToken) return false;
        this.licenses = result.items.map(License.dataConverter(this.city));
        this.total = result.total;
        this.pageStart = result.offset;
        this.cursor = result.cursor;
        this.nextCursor = result.nextCursor;
        this.previousCursor = result.previousCursor;
        this.firstCursor = result.firstCursor;
        this.lastCursor = result.lastCursor;
        const openSingle = this.openSingleSearchResult;
        this.openSingleSearchResult = false;
        if (openSingle && result.total === 1 && !this.selectedLicenseId) {
          this.selectLicense(this.licenses[0].id);
        }
        return true;
      } catch (error) {
        if (token !== this.loadToken) return false;
        this.loadError = error;
        this.licenses = [];
        this.total = 0;
        this.pageStart = 0;
        this.cursor = this.nextCursor = this.previousCursor = null;
        this.firstCursor = this.lastCursor = null;
        return false;
      } finally {
        if (token === this.loadToken) {
          this.loaded = true;
          this.filtering = false;
        }
      }
    },
    changePage(cursor) {
      if (this.filtering || !cursor) return false;
      return this.reloadLicenses({ cursor });
    },
    search(query) {
      this.query = query;
      this.openSingleSearchResult = Boolean(query.trim());
      return this.reloadLicenses({ offset: 0 });
    },
    applyFilterChanges() {
      this.filter = this.draftFilter;
      this.filterLicenses();
    },
    cancelFilterChanges() {
      this.draftFilter = this.filter.copy();
    },
    resetFilters() {
      this.draftFilter = this.makeDefaultFilter();
    },
    async nextLicense(isBackward) {
      if (this.filtering || !this.total) return;
      const selectedId = this.selectedLicenseId;
      const index = this.licenses.findIndex(license => license.id === this.selectedLicenseId);
      let target = index + (isBackward ? -1 : 1);
      if (index < 0) {
        const cursor = isBackward ? this.lastCursor : this.firstCursor;
        if (!await this.reloadLicenses({ cursor })) return;
        target = isBackward ? this.licenses.length - 1 : 0;
      } else if (target < 0 || target >= this.licenses.length) {
        const cursor = isBackward ? (this.previousCursor || this.lastCursor) : (this.nextCursor || this.firstCursor);
        if (!await this.reloadLicenses({ cursor })) return;
        target = isBackward ? this.licenses.length - 1 : 0;
      }
      if (selectedId === this.selectedLicenseId && this.licenses[target]) this.selectLicense(this.licenses[target].id);
    },
    closeDetail(message) {
      this.selectLicense(null);
      if (message && message.title && message.body) {
        this.$showToast(`<div class="fw-bold">${message.title}</div><div>${message.body}</div>`);
      }
    },
    clearExportUrl() {
      if (this.exportUrl) URL.revokeObjectURL(this.exportUrl);
      this.exportUrl = null;
    },
    addLicense() {
      this.editLicense = new License(this.city);
    },
    licenseCreated(license) {
      this.editLicense = null;
      this.selectLicense(license.id);
    },
    selectLicense(id) {
      if (id) {
        this.$router.push({
          name: 'license-detail',
          params: {
            selectedLicenseId: id
          },
          query: this.$route.query
        });
      } else {
        this.$router.push({
          name:'licenses',
          query: this.$route.query
        });
      }
    },
    showLocation(latlng) {
      if (this.$refs.map) {
        this.$refs.map.setCenter(latlng, 18);
      }
    },
    updatePanel(panel) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          panel: panel || undefined
        }
      });
    },

    filterLicenses() {
      this.openSingleSearchResult = false;
      return this.reloadLicenses({ offset: 0 });
    },
    async exportLicenses() {
      if (this.exporting || !this.city || !this.filter || !this.isAuthenticated) return;
      this.exporting = true;
      this.exportError = null;
      const city = this.city;
      const token = this.cityLoadToken;
      const options = this.searchOptions();
      try {
        const { items } = await licenseRepository.exportLicenses(city.id, options);
        if (token !== this.cityLoadToken || !this.isAuthenticated) return;
        const licenses = items.map(License.dataConverter(city));
        const blob = generateCsvBlob(',',
          ['License Number', 'License Type', 'Date Issued', 'Status', 'Parcel ID', 'Street Number', 'Street Name', 'Unit', 'Address', 'Owner', 'Email', 'Phone'],
          licenses.map(license => [
            license.number + ' ', license.licenseTypeLabel, this.formatDate(license.dateIssued, ' '),
            license.status, license.propertyId, license.addressStreetNumber || '-',
            license.addressStreetName || '-', license.addressUnit || '', license.addressFormatted,
            (license.contact?.owners || []).join(', '), license.contact?.email, license.contact?.phone
          ])
        );
        this.clearExportUrl();
        this.exportUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = this.exportUrl;
        link.download = `${city.id}_licenses.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        if (token === this.cityLoadToken) this.exportError = error;
      } finally {
        this.exporting = false;
      }
    }
  }
}
</script>
